const CHUNK_RELOAD_KEY = 'app.chunk.reload'
const RELOAD_DEBOUNCE_MS = 30_000

/**
 * 신규 배포로 구버전 chunk가 사라져 dynamic import가 실패하거나 빈 모듈로 resolve될 때
 * 1회 reload로 복구한다.
 *
 * 30초 내 재시도는 무한 새로고침 루프를 막기 위해 건너뛰고 false를 반환한다 —
 * 호출부는 이때 원본 에러를 다시 던져 Sentry로 보고(CDN 전파 지연 등 실제 장애 탐지)하게 한다.
 *
 * @returns reload를 예약했으면 true, debounce로 건너뛰었으면 false
 */
export function reloadForStaleChunk(): boolean {
  const now = Date.now()

  let prev = 0
  try {
    prev = Number(window.sessionStorage?.getItem(CHUNK_RELOAD_KEY) ?? '0')
  } catch {
    /* sessionStorage 미지원/차단 환경 — prev=0으로 진행 */
  }
  if (now - prev < RELOAD_DEBOUNCE_MS) return false

  try {
    window.sessionStorage?.setItem(CHUNK_RELOAD_KEY, String(now))
  } catch {
    /* sessionStorage 미지원/차단 환경 */
  }
  window.location.reload()
  return true
}

/**
 * `@loadable/component`의 lazy() loader를 감싸 chunk 로드 실패를 복구한다.
 *
 * - import가 reject되거나(ChunkLoadError) 빈 모듈(undefined/null)로 resolve되면
 *   `reloadForStaleChunk()`로 복구한다. 후자는 @loadable의 `defaultResolveComponent`가
 *   `undefined.__esModule` 접근으로 폭발하던 경로(OFFICEMAIL-WEB-ACCESS-J1/HT/HR/K3)를 사전 차단한다.
 * - reload가 예약되면 fallback을 유지하기 위해 영원히 pending인 promise를 반환한다(페이지가 곧 언로드됨).
 * - reload가 debounce로 차단되면(반복 실패) 원본 에러를 다시 던져 Sentry로 보고한다.
 */
export function loadWithChunkRetry<T>(importFn: () => Promise<T>): () => Promise<T> {
  const recover = (error: unknown): Promise<T> => {
    if (reloadForStaleChunk()) {
      // reload 예약됨 — 페이지 언로드까지 loadable fallback 유지
      return new Promise<T>(() => {})
    }
    throw error
  }

  return async () => {
    let loadedModule: T
    try {
      loadedModule = await importFn()
    } catch (error) {
      return recover(error)
    }

    if (loadedModule == null) {
      return recover(new Error('Dynamic import resolved to an empty module'))
    }
    return loadedModule
  }
}
